/**
 * Every URL the application can be at, in one place.
 *
 * In `shared` rather than in `app` because pages and widgets need to build links too, and
 * the layer rule in eslint.config.js — rightly — stops them importing from `app`. The app
 * layer re-exports this, so `ROUTES` keeps working wherever it is already used.
 */
export const ROUTES = {
  dashboard: '/',
  games: '/games',
  gameDetails: (slug: string) => `/games/${slug}`,
  /**
   * The playthrough is a query parameter, not part of the path: the quest is the thing being
   * looked at, and it reads the same with or without a run selected. A path segment would
   * make the two versions look like different pages to share.
   */
  /**
   * @param from Where to come back to, as the run page's own query string — see
   * {@link ROUTES.returnTo}. Carried rather than guessed: the quest page is reached from the
   * navigation board and from a location's contents, and "back" means a different screen in
   * each case. Without it every return landed on "What now", which is not where anyone who
   * came from the map was standing.
   */
  quest: (slug: string, code: string, playthroughId?: string, from?: string) => {
    const query = new URLSearchParams();

    if (playthroughId) query.set('playthrough', playthroughId);
    if (from) query.set('from', from);

    const search = query.toString();

    return `/games/${slug}/quests/${encodeURIComponent(code)}${search ? `?${search}` : ''}`;
  },
  /**
   * The query string that brings a reader back to where they were standing.
   *
   * The tab alone is not where they were: every tab has its own idea of "here". The map has a
   * location open, the board is showing one kind of thing out of six. Anything a tab restores
   * itself from the address can be named here, so the extras are a bag rather than a growing
   * list of positional parameters — the next tab that needs one adds it without touching this.
   *
   * Only what the tab reads back from the URL is worth putting in. A value the tab keeps in
   * `useState` cannot be restored from a query string it never looks at.
   */
  returnTo: (tab: string, extra?: Record<string, string | undefined>) => {
    const query = new URLSearchParams({ tab });

    for (const [key, value] of Object.entries(extra ?? {})) {
      if (value) query.set(key, value);
    }

    return query.toString();
  },
  /**
   * The page for an object that is not a quest. Epic 16 §16.6.
   *
   * The kind is in the path rather than guessed from the code: codes are unique per game
   * *and kind*, so `boss.radahn` and an item of the same code could both exist, and a route
   * that had to try seven tables would answer differently depending on the order it tried.
   */
  object: (slug: string, type: string, code: string, playthroughId?: string, from?: string) => {
    const query = new URLSearchParams();

    if (playthroughId) query.set('playthrough', playthroughId);
    if (from) query.set('from', from);

    const search = query.toString();

    return `/games/${slug}/objects/${type}/${encodeURIComponent(code)}${search ? `?${search}` : ''}`;
  },
  /**
   * The knowledge card for one object. The tab is part of the link on purpose: the browser
   * that reads `object` only exists on the knowledge tab, so without it the link lands on
   * the overview and appears to do nothing.
   */
  knowledgeObject: (slug: string, objectType: string, code: string) =>
    `/games/${slug}?tab=knowledge&object=${encodeURIComponent(`${objectType}:${code}`)}`,
  login: '/login',
  register: '/register',
  profile: '/profile',
  playthroughs: '/playthroughs',
  /**
   * `tab` is spelled out where it matters. The page falls back to "What now" on its own,
   * but a link that relies on someone else's default breaks quietly the day that default
   * moves.
   */
  playthroughDetails: (id: string, tab?: string) =>
    `/playthroughs/${id}${tab ? `?tab=${tab}` : ''}`,
  /** The same page, restored from a {@link ROUTES.returnTo} string rather than a bare tab. */
  playthroughAt: (id: string, search?: string | null) =>
    `/playthroughs/${id}${search ? `?${search}` : ''}`,
  admin: '/admin',
} as const;

/**
 * The address of an object's own page.
 *
 * Every kind has one now — Epic 16 §16.6 gave the other six a read-only page with the whole
 * dependency graph on it. A quest keeps its own richer page, with objectives and a
 * walkthrough; everything else shares one.
 *
 * Still asked here rather than tested for `'Quest'` at each call site: which kinds have which
 * page is one fact, and the screens that link to it should not each hold an opinion. Typed as
 * a plain string because `shared` sits below `entities` and cannot name `KnowledgeType`.
 */
export function objectPage(
  type: string,
  slug: string,
  code: string,
  playthroughId?: string,
  from?: string,
): string {
  return type === 'Quest'
    ? ROUTES.quest(slug, code, playthroughId, from)
    : ROUTES.object(slug, type, code, playthroughId, from);
}
